import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Grid,
  Divider,
  Tooltip,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Search,
  QrCodeScanner,
  Download,
  PictureAsPdf,
  TableChart,
  Description,
  Science,
  AccessTime,
  TrendingUp,
  CheckCircle,
  RadioButtonUnchecked,
  Warning,
  ExpandMore,
  Timeline as TimelineIcon,
  FlashOn,
  LocalShipping,
  Build,
  BarChart,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { SampleStatus } from '../services/samplesService';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

interface SearchResult {
  samples: any[];
  total: number;
  page: number;
  totalPages: number;
}

const RechercheRapide: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [selectedSample, setSelectedSample] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedSample, setExpandedSample] = useState<string | null>(null);

  const statusLabels: Record<string, string> = {
    'RECEPTION': 'Réception',
    'PREPARATION_MECANIQUE': 'Préparation Mécanique',
    'LABORATOIRE_SAA_PREP_CHIMIQUE': 'SAA - Prep Chimique',
    'LABORATOIRE_SAA_ANALYSE': 'SAA - Analyse',
    'LABORATOIRE_ICP_PREP_CHIMIQUE': 'ICP - Prep Chimique',
    'LABORATOIRE_ICP_ANALYSE': 'ICP - Analyse',
    'LABORATOIRE_AU_PREP_CHIMIQUE': 'AU - Prep Chimique',
    'LABORATOIRE_AU_ANALYSE': 'AU - Analyse',
    'LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE': 'Volumétrie - Prep Chimique',
    'LABORATOIRE_VOLUMETRIE_ANALYSE': 'Volumétrie - Analyse',
    'VALIDATION_SERVICE': 'Validation Service',
    'GENERATION_CERTIFICAT': 'Génération Certificat',
    'PRET': 'Prêt',
  };

  const getStatusColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      'RECEPTION': theme.palette.info.main,
      'PREPARATION_MECANIQUE': theme.palette.warning.main,
      'LABORATOIRE_ICP_PREP_CHIMIQUE': theme.palette.primary.main,
      'LABORATOIRE_ICP_ANALYSE': theme.palette.primary.dark,
      'LABORATOIRE_AU_PREP_CHIMIQUE': theme.palette.secondary.main,
      'LABORATOIRE_AU_ANALYSE': theme.palette.secondary.dark,
      'LABORATOIRE_SAA_PREP_CHIMIQUE': theme.palette.error.main,
      'LABORATOIRE_SAA_ANALYSE': theme.palette.error.dark,
      'VALIDATION_SERVICE': theme.palette.success.light,
      'PRET': theme.palette.success.main,
    };
    return colorMap[status] || theme.palette.grey[500];
  };

  const handleSearch = useCallback(async (page: number = 1) => {
    if (!searchTerm.trim()) {
      setError('Veuillez entrer un terme de recherche');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get<SearchResult>('/samples/search-history', {
        params: {
          q: searchTerm,
          page: page.toString(),
          limit: '50',
        },
      });

      setSearchResults(response);
      setCurrentPage(page);
      
      // If only one result, automatically select it
      if (response.samples.length === 1) {
        setSelectedSample(response.samples[0]);
        setExpandedSample(response.samples[0].id);
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.message || 'Erreur lors de la recherche');
      setSearchResults(null);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleScanQR = () => {
    // TODO: Integrate with QR scanner
    alert('QR Scanner integration coming soon');
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    handleSearch(page);
  };

  const exportToExcel = (sample: any) => {
    const ws_data = [
      ['Recherche Rapide - Historique d\'échantillon'],
      [],
      ['ID Échantillon', sample.sample_id],
      ['Référence Client', sample.external_id || 'N/A'],
      ['Client', sample.client?.nom || 'N/A'],
      ['Type', sample.type_echantillon],
      ['Statut Actuel', statusLabels[sample.statut] || sample.statut],
      ['Durée Totale', sample.calculatedMetrics?.totalDurationFormatted || 'N/A'],
      [],
      ['HISTORIQUE DES TRANSITIONS'],
      ['Date', 'De', 'Vers', 'Durée', 'Utilisateur'],
    ];

    sample.transitions?.forEach((t: any) => {
      ws_data.push([
        new Date(t.transition_date).toLocaleString('fr-FR'),
        statusLabels[t.from_status] || t.from_status || 'Création',
        statusLabels[t.to_status] || t.to_status,
        t.duration_minutes ? `${t.duration_minutes} min` : 'N/A',
        'Utilisateur',
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Historique');
    XLSX.writeFile(wb, `recherche-rapide-${sample.sample_id}.xlsx`);
  };

  const exportToPDF = (sample: any) => {
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Recherche Rapide - Historique d\'échantillon', 14, 20);
    
    doc.setFontSize(12);
    doc.text(`ID: ${sample.sample_id}`, 14, 35);
    doc.text(`Client: ${sample.client?.nom || 'N/A'}`, 14, 42);
    doc.text(`Statut: ${statusLabels[sample.statut] || sample.statut}`, 14, 49);
    doc.text(`Durée totale: ${sample.calculatedMetrics?.totalDurationFormatted || 'N/A'}`, 14, 56);

    if (sample.bgmRouting?.isBGMSample) {
      doc.setTextColor(255, 140, 0);
      doc.text('⚡ BGM MULTI-LAB', 14, 63);
      doc.setTextColor(0, 0, 0);
    }

    const tableData = sample.transitions?.map((t: any) => [
      new Date(t.transition_date).toLocaleString('fr-FR'),
      statusLabels[t.from_status] || t.from_status || 'Création',
      statusLabels[t.to_status] || t.to_status,
      t.duration_minutes ? `${t.duration_minutes} min` : 'N/A',
    ]) || [];

    (doc as any).autoTable({
      head: [['Date', 'De', 'Vers', 'Durée']],
      body: tableData,
      startY: 75,
      theme: 'grid',
    });

    doc.save(`recherche-rapide-${sample.sample_id}.pdf`);
  };

  const exportToCSV = (sample: any) => {
    const csvData = [
      ['ID Échantillon', 'Référence Client', 'Client', 'Type', 'Statut', 'Durée Totale'],
      [
        sample.sample_id,
        sample.external_id || 'N/A',
        sample.client?.nom || 'N/A',
        sample.type_echantillon,
        statusLabels[sample.statut] || sample.statut,
        sample.calculatedMetrics?.totalDurationFormatted || 'N/A'
      ],
      [],
      ['Date', 'De', 'Vers', 'Durée (min)', 'Utilisateur'],
      ...(sample.transitions?.map((t: any) => [
        new Date(t.transition_date).toLocaleString('fr-FR'),
        statusLabels[t.from_status] || t.from_status || 'Création',
        statusLabels[t.to_status] || t.to_status,
        t.duration_minutes || 'N/A',
        'Utilisateur'
      ]) || [])
    ];

    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `recherche-rapide-${sample.sample_id}.csv`;
    link.click();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Search sx={{ fontSize: 40 }} />
          Recherche Rapide
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Recherchez un échantillon par numéro de lot, référence client ou QR code pour voir son historique complet
        </Typography>
      </Box>

      {/* Search Bar */}
      <Paper sx={{ p: 3, mb: 4, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)` }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={10}>
            <TextField
              fullWidth
              placeholder="Rechercher par numéro de lot (AFL-XXXX, R-XXXX), référence client, ou QR code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Scanner QR Code">
                      <IconButton onClick={handleScanQR} color="primary">
                        <QrCodeScanner />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => handleSearch()}
              disabled={loading || !searchTerm.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
            >
              Rechercher
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search Results */}
      {searchResults && searchResults.samples.length > 0 && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {searchResults.total} résultat{searchResults.total > 1 ? 's' : ''} trouvé{searchResults.total > 1 ? 's' : ''}
            </Typography>
            {searchResults.totalPages > 1 && (
              <Pagination
                count={searchResults.totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            )}
          </Box>

          {searchResults.samples.map((sample) => (
            <Accordion
              key={sample.id}
              expanded={expandedSample === sample.id}
              onChange={() => setExpandedSample(expandedSample === sample.id ? null : sample.id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: '100%' }}>
                  <Typography variant="h6" fontWeight="bold">
                    {sample.sample_id}
                  </Typography>
                  
                  {sample.bgmRouting?.isBGMSample && (
                    <Chip
                      icon={<FlashOn />}
                      label="BGM MULTI-LAB"
                      color="warning"
                      size="small"
                    />
                  )}
                  
                  <Chip
                    label={statusLabels[sample.statut] || sample.statut}
                    size="small"
                    sx={{
                      backgroundColor: alpha(getStatusColor(sample.statut), 0.1),
                      color: getStatusColor(sample.statut),
                      fontWeight: 'bold',
                    }}
                  />
                  
                  <Typography variant="body2" color="text.secondary">
                    Client: {sample.client?.nom || 'N/A'}
                  </Typography>
                  
                  {sample.external_id && (
                    <Typography variant="body2" color="text.secondary">
                      Ref: {sample.external_id}
                    </Typography>
                  )}
                  
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                    <AccessTime sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    {sample.calculatedMetrics?.totalDurationFormatted || 'N/A'}
                  </Typography>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                {/* Sample Details Card */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Informations Générales</Typography>
                        <Divider sx={{ my: 1 }} />
                        <Stack spacing={1}>
                          <Box><strong>ID:</strong> {sample.sample_id}</Box>
                          <Box><strong>Client:</strong> {sample.client?.nom || 'N/A'}</Box>
                          <Box><strong>Référence Client:</strong> {sample.external_id || 'N/A'}</Box>
                          <Box><strong>Type:</strong> {sample.type_echantillon}</Box>
                          <Box><strong>Priorité:</strong> {sample.priorite}</Box>
                          <Box><strong>Date Création:</strong> {new Date(sample.date_creation).toLocaleString('fr-FR')}</Box>
                        </Stack>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Métriques</Typography>
                        <Divider sx={{ my: 1 }} />
                        <Stack spacing={1}>
                          <Box><strong>Statut Actuel:</strong> {statusLabels[sample.statut] || sample.statut}</Box>
                          <Box><strong>Durée Totale:</strong> {sample.calculatedMetrics?.totalDurationFormatted || 'N/A'}</Box>
                          <Box><strong>Nombre de Transitions:</strong> {sample.calculatedMetrics?.transitionCount || 0}</Box>
                          <Box><strong>Événements Suivis:</strong> {sample.calculatedMetrics?.trackingEventCount || 0}</Box>
                          {sample.bgmRouting?.isBGMSample && (
                            <>
                              <Box sx={{ color: theme.palette.warning.main }}><strong>⚡ Type BGM Multi-Lab</strong></Box>
                              <Box><strong>Labs Actifs:</strong> {sample.activeLabs?.join(', ') || 'Aucun'}</Box>
                            </>
                          )}
                        </Stack>
                      </Grid>
                    </Grid>

                    {/* Export Buttons */}
                    <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        startIcon={<PictureAsPdf />}
                        variant="outlined"
                        color="error"
                        onClick={() => exportToPDF(sample)}
                      >
                        Exporter PDF
                      </Button>
                      <Button
                        startIcon={<TableChart />}
                        variant="outlined"
                        color="success"
                        onClick={() => exportToExcel(sample)}
                      >
                        Exporter Excel
                      </Button>
                      <Button
                        startIcon={<Description />}
                        variant="outlined"
                        color="info"
                        onClick={() => exportToCSV(sample)}
                      >
                        Exporter CSV
                      </Button>
                    </Box>
                  </CardContent>
                </Card>

                {/* BGM Routing Details */}
                {sample.bgmRouting?.isBGMSample && (
                  <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: theme.palette.warning.main }}>
                        <FlashOn /> Routage BGM Multi-Lab
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>Laboratoire ICP (8 éléments)</Typography>
                          <Stack spacing={0.5}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {sample.bgmRouting.icpPrepCompleted ? <CheckCircle color="success" fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
                              <Typography variant="body2">Préparation Chimique</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {sample.bgmRouting.icpAnalysisCompleted ? <CheckCircle color="success" fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
                              <Typography variant="body2">Analyse</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {sample.bgmRouting.icpValidationCompleted ? <CheckCircle color="success" fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
                              <Typography variant="body2">Validation</Typography>
                            </Box>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>Laboratoire AU (1 élément)</Typography>
                          <Stack spacing={0.5}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {sample.bgmRouting.auPrepCompleted ? <CheckCircle color="success" fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
                              <Typography variant="body2">Préparation Chimique</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {sample.bgmRouting.auAnalysisCompleted ? <CheckCircle color="success" fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
                              <Typography variant="body2">Analyse</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {sample.bgmRouting.auValidationCompleted ? <CheckCircle color="success" fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
                              <Typography variant="body2">Validation</Typography>
                            </Box>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Timeline */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimelineIcon />
                      Historique des Transitions
                    </Typography>
                    
                    {sample.transitions && sample.transitions.length > 0 ? (
                      <Timeline position="alternate">
                        {sample.transitions.map((transition: any, index: number) => (
                          <TimelineItem key={index}>
                            <TimelineOppositeContent color="text.secondary">
                              {new Date(transition.transition_date).toLocaleString('fr-FR')}
                              {transition.duration_minutes && (
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                  <AccessTime sx={{ fontSize: 12, verticalAlign: 'middle' }} /> {transition.duration_minutes} min
                                </Typography>
                              )}
                            </TimelineOppositeContent>
                            <TimelineSeparator>
                              <TimelineDot sx={{ bgcolor: getStatusColor(transition.to_status) }}>
                                {index === sample.transitions.length - 1 ? <CheckCircle fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
                              </TimelineDot>
                              {index < sample.transitions.length - 1 && <TimelineConnector />}
                            </TimelineSeparator>
                            <TimelineContent>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {statusLabels[transition.to_status] || transition.to_status}
                              </Typography>
                              {transition.from_status && (
                                <Typography variant="caption" color="text.secondary">
                                  De: {statusLabels[transition.from_status] || transition.from_status}
                                </Typography>
                              )}
                              {transition.reason && (
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                  {transition.reason}
                                </Typography>
                              )}
                            </TimelineContent>
                          </TimelineItem>
                        ))}
                      </Timeline>
                    ) : (
                      <Alert severity="info">Aucune transition enregistrée</Alert>
                    )}
                  </CardContent>
                </Card>

                {/* Tracking Events Table */}
                {sample.historique_tracking && sample.historique_tracking.length > 0 && (
                  <Card sx={{ mt: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Événements de Suivi
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Action</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell>Localisation</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sample.historique_tracking.map((event: any, index: number) => (
                              <TableRow key={index}>
                                <TableCell>{new Date(event.action_date).toLocaleString('fr-FR')}</TableCell>
                                <TableCell>{event.action_type}</TableCell>
                                <TableCell>{event.action_description}</TableCell>
                                <TableCell>{event.location || 'N/A'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}

      {/* No Results */}
      {searchResults && searchResults.samples.length === 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          Aucun échantillon trouvé pour "{searchTerm}"
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && !searchResults && !error && (
        <Paper sx={{ p: 8, textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
          <Search sx={{ fontSize: 80, color: theme.palette.action.disabled, mb: 2 }} />
          <Typography variant="h5" gutterBottom color="text.secondary">
            Commencez une recherche
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Entrez un numéro de lot, une référence client ou scannez un QR code
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default RechercheRapide;

